<template>
  <div v-if="show" class="job-question-page">
    <QuestionForm
      class="question-page"
      ref="questionForm"
      :formLine="questionFormLine"
      :data="questionData"
      presentation="candidate-mobile"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import QuestionForm from '@/components/mobile/questionFrom.vue'
import { QUESTION_TYPE as OPEN_QUESTION_TYPE } from '@/constant/openQuestion'
import mixin from './mixin'
import { validateIDCardNumberByVant } from '@/utils/validator'
import { STEP_TYPE } from '@/constant/candidate'
import {
  OPEN_QUESTION_TREE_MODE,
  clearChildTreeAnswers,
  detectTreeMode,
  enhanceTreeQuestion,
  getVisibleTreeQuestions
} from '@/utils/openQuestionTree'

export default {
  mixins: [mixin],
  components: {
    QuestionForm
  },
  props: {
    data: {
      type: Array,
      default: null
    },
    meta: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      questionFormLine: [],
      questionData: {},
      allQuestionList: [],
      previousAnswerMap: {},
      isElcCompany: false,
      mode: OPEN_QUESTION_TREE_MODE.FLAT
    }
  },
  computed: {
    companyId () {
      return this.$route.query.company_id
    },
    isTreeMode () {
      return this.mode === OPEN_QUESTION_TREE_MODE.TREE
    }
  },
  methods: {
    getQuestionFieldName (question) {
      return `question-${question.id}`
    },
    getDefaultQuestionValue (question) {
      if ([OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(question.type)) {
        return {
          videoKey: null,
          description: null
        }
      }
      if ([OPEN_QUESTION_TYPE.CHECKBOX, OPEN_QUESTION_TYPE.MULTIPLE_SELECT].includes(question.type)) {
        return []
      }
      return ''
    },
    normalizeQuestionList (questionList) {
      return _.map(questionList || [], item => {
        const question = enhanceTreeQuestion({ ...item })
        const result = item.result || {}
        if (item.type === OPEN_QUESTION_TYPE.VIDEO || item.type === OPEN_QUESTION_TYPE.ATTACHMENT) {
          question.answer = {
            videoKey: result?.video_resource_key || null,
            description: result?.value || null
          }
        } else if (item.type === OPEN_QUESTION_TYPE.CHECKBOX || item.type === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
          question.answer = result?.options || []
        } else if (item.type === OPEN_QUESTION_TYPE.SELECT || item.type === OPEN_QUESTION_TYPE.SINGLE_SELECT) {
          question.answer = result?.value ? parseInt(result.value) : null
        } else {
          question.answer = result?.value || ''
        }
        return question
      })
    },
    buildQuestionLine (question) {
      const uploaderFileSize = 200 * 1024 * 1024
      const uploaderFileTypeList = ['video/mp4', 'video/x-msvideo', 'video/ogg', 'video/quicktime']
      const elType = question.type
      const line = {
        id: question.id,
        title: question.title.replace(/[\n]+$/, ''),
        type: question.type,
        name: this.getQuestionFieldName(question),
        required: question.required,
        tree: this.isTreeMode ? this.getQuestionTreeDisplay(question) : null,
        fileSize: uploaderFileSize,
        uploaderFileSize,
        maxSize: 1,
        rules: question.required
          ? [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
          : []
      }
      if (elType === OPEN_QUESTION_TYPE.VIDEO) {
        line.fileType = uploaderFileTypeList
        if (this.isElcCompany) {
          line.capture = 'camera'
          line.fileType = 'video/*'
          line.byCamera = true
        }
        line.elType = 'file'
        line.duration = question.duration
        line.tips = [
          this.$t(`${this.i18nPrefix}.message.uploadTip.${this.isElcCompany ? 'videoUploadTipsForELC' : 'videoUploadTips'}`),
          this.$t(`${this.i18nPrefix}.message.uploadTip.openQuestionVideo`, { duration: question.duration })
        ]
        line.btnName = this.$t(`${this.i18nPrefix}.btn.upload.selectFile`)
        line.placeholder = this.$t(`${this.i18nPrefix}.placeholder.videoDescription`)
      } else if (elType === OPEN_QUESTION_TYPE.ATTACHMENT) {
        line.elType = 'file'
        line.tip = this.$t(`${this.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
        line.btnName = this.$t(`${this.i18nPrefix}.btn.upload.selectFile`)
        line.placeholder = this.$t(`${this.i18nPrefix}.placeholder.fileDescription`)
      } else if (elType === OPEN_QUESTION_TYPE.SELECT || elType === OPEN_QUESTION_TYPE.SINGLE_SELECT) {
        line.elType = 'radio'
        line.direction = 'vertical'
        if (this.isTreeMode) line.optionSlot = `tree-options-${question.id}`
        line.options = {
          data: question.option || question.options || [],
          labelKey: 'description',
          valueKey: 'id'
        }
      } else if (elType === OPEN_QUESTION_TYPE.CHECKBOX || elType === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
        line.elType = 'checkbox'
        line.direction = 'vertical'
        if (this.isTreeMode) line.optionSlot = `tree-options-${question.id}`
        line.options = {
          data: question.option || question.options || [],
          labelKey: 'description',
          valueKey: 'id'
        }
      } else if (elType === OPEN_QUESTION_TYPE.TEXT) {
        line.elType = 'question-text'
        line.autosize = { minHeight: 46 }
        if (question.is_id_number) {
          line.autosize = { minHeight: 24 }
          line.rows = '1'
          line.rules.push({ validator: this.validateIDCardNumber, message: this.$t('common.message.invalidIDCardNumber'), trigger: 'onBlur' })
        }
      } else if (elType === OPEN_QUESTION_TYPE.DATE) {
        line.type = 'text'
        line.elType = 'date'
        line.dateType = 'date'
      }
      return line
    },
    getQuestionTreeDisplay (question) {
      const relation = this.getQuestionParentRelation(question)
      return {
        parentQuestionId: relation ? String(relation.parentQuestionId) : null,
        parentOptionId: relation ? String(relation.parentOptionId) : null
      }
    },
    getQuestionParentRelation (question) {
      const questionId = question.id
      for (const parentQuestion of this.allQuestionList) {
        const optionTreeMap = _.get(parentQuestion, 'treeMeta.optionTreeMap', [])
        const option = _.find(optionTreeMap, item => String(item.nextQuestionId) === String(questionId))
        if (option) {
          return {
            parentQuestionId: parentQuestion.id,
            parentOptionId: option.id
          }
        }
      }
      return null
    },
    refreshQuestionLine () {
      const visibleQuestions = this.isTreeMode ? getVisibleTreeQuestions(this.allQuestionList) : this.allQuestionList
      const questionData = { ...this.questionData }
      _.forEach(this.allQuestionList, question => {
        const name = this.getQuestionFieldName(question)
        if (questionData[name] === undefined) {
          questionData[name] = _.cloneDeep(question.answer)
        } else {
          question.answer = questionData[name]
        }
      })
      this.questionData = questionData
      this.questionFormLine = _.map(visibleQuestions, question => this.buildQuestionLine(question))
      this.previousAnswerMap = _.reduce(this.allQuestionList, (result, question) => {
        result[this.getQuestionFieldName(question)] = _.cloneDeep(question.answer)
        return result
      }, {})
    },
    syncAnswerFromForm () {
      if (_.isEmpty(this.allQuestionList)) {
        return
      }
      let hasTreeChanged = false
      _.forEach(this.allQuestionList, question => {
        const fieldName = this.getQuestionFieldName(question)
        const currentAnswer = _.cloneDeep(this.questionData[fieldName])
        const previousAnswer = this.previousAnswerMap[fieldName]
        question.answer = currentAnswer
        if (this.isTreeMode && !_.isEqual(currentAnswer, previousAnswer)) {
          clearChildTreeAnswers(this.allQuestionList, question.id, childQuestion => {
            const childFieldName = this.getQuestionFieldName(childQuestion)
            childQuestion.answer = this.getDefaultQuestionValue(childQuestion)
            this.$set(this.questionData, childFieldName, _.cloneDeep(childQuestion.answer))
          })
          hasTreeChanged = true
        }
      })
      if (hasTreeChanged) {
        this.refreshQuestionLine()
      }
    },
    validateIDCardNumber (value, rule) {
      const result = validateIDCardNumberByVant(value)
      if (result.msg) {
        rule.message = result.msg
      }
      return result.valid
    },
    getAnswer () {
      const answer = {}
      _.forEach(this.questionFormLine, line => {
        const questionId = line.id
        const name = line.name
        let questionAnswer = this.questionData[name]
        if (line.type === OPEN_QUESTION_TYPE.CHECKBOX || line.type === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
          questionAnswer = questionAnswer.toString()
        } else if (line.type === OPEN_QUESTION_TYPE.ATTACHMENT || line.type === OPEN_QUESTION_TYPE.VIDEO) {
          questionAnswer = {
            videoKey: questionAnswer.videoKey,
            description: questionAnswer.description || null
          }
        }
        answer[questionId] = questionAnswer
      })
      return answer
    },
    async submit () {
      const form = await this.getForm()
      if (!form) {
        return false
      }
      const result = await form.submit()
      return result
    },
    async getForm () {
      if (this.show) {
        let form = this.$refs.questionForm
        if (!form) {
          await this.$nextTick()
          form = this.$refs.questionForm
        }
        return form
      }
      return null
    }
  },
  watch: {
    data: {
      immediate: true,
      handler () {
        this.isElcCompany = this.resumeEditorSetting?.[STEP_TYPE.JOB_QUESTIONNAIRE].isElcCompany
        this.allQuestionList = this.normalizeQuestionList(this.data)
        this.mode = detectTreeMode({ questions: this.allQuestionList, meta: this.meta })
        this.refreshQuestionLine()
      }
    },
    questionData: {
      deep: true,
      handler () {
        this.syncAnswerFromForm()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-question-page {
  border-top: 10px solid #f7f7f7;
}
</style>
